import React, { Fragment } from 'react';
import type { WRelatedLinkView } from '@zola/svc-web-api-ts-client';

import AccordionV2 from '../AccordionV2';
import T from '../../typography/Text';

import {
  ButtonWrapper,
  ContentWrapper,
  SectionWrapper,
  StyledHr,
  StyledHTitle3,
  Wrapper,
  InnerList,
  InnerListItem,
  FAQSListWrapper,
  AdviceContentWrapper,
} from './SupportAccordion.styles';
import { ButtonV3 } from '../ButtonV3';
import FAQItem, { FAQItemProps } from './FAQItem';
import ReviewItem, { ReviewItemProps } from './ReviewItem';
import AdviceItem, { AdviceItemProps } from './AdviceItem';
import { PreAuthCarouselCardProps } from './PreAuthCarouselCard';

export type SupportAccordionSection = 'faqs' | 'reviews' | 'advice';

export type SupportAccordionProps = {
  className?: string;
  faqs?: FAQItemProps[];
  reviews?: ReviewItemProps[];
  advice?: {
    cards?: PreAuthCarouselCardProps[];
    links?: WRelatedLinkView[];
  };
  collapsed?: boolean;
  openSections?: SupportAccordionSection[];
};

const sectionIds: Record<SupportAccordionSection, number> = {
  faqs: 1,
  reviews: 2,
  advice: 3,
};

const SupportAccordion: React.FC<SupportAccordionProps> = (props: SupportAccordionProps) => {
  const { className, faqs, reviews, advice, collapsed = true, openSections } = props;
  let adviceItems: AdviceItemProps[] = [];
  const initialExpandedItems = openSections?.map((section) => sectionIds[section]);

  if (advice?.links) {
    // limit to top 9 links and separate into 3 sections
    const top9Links = advice.links.slice(0, 9);
    adviceItems = new Array(Math.ceil(top9Links.length / 3))
      .fill({ links: [] })
      .map(() => ({ links: top9Links.splice(0, 3) }));
  } else if (advice?.cards) {
    adviceItems = advice.cards.map((card) => ({ card }));
  }

  const items = [];

  if (faqs && faqs.length > 0) {
    items.push({
      hed: 'FAQ',
      dek: (
        <FAQSListWrapper>
          {faqs.map((faq, index) => (
            <InnerListItem key={`faq-item-${index}`}>
              <FAQItem question={faq.question} answer={faq.answer} />
            </InnerListItem>
          ))}
        </FAQSListWrapper>
      ),
      id: 1,
    });
  }
  if (reviews && reviews.length > 0) {
    items.push({
      hed: 'Reviews',
      dek: (
        <SectionWrapper>
          <InnerList>
            {reviews.map((review, index) => (
              <Fragment key={`review-item-${index}`}>
                <InnerListItem>
                  <ReviewItem {...review} />
                </InnerListItem>
                {index < reviews.length - 1 && <StyledHr aria-hidden />}
              </Fragment>
            ))}
          </InnerList>
          <ButtonWrapper>
            <ButtonV3
              size="large"
              variant="secondary"
              component="a"
              href="/wedding-registry/couple-reviews"
            >
              Read More
            </ButtonV3>
          </ButtonWrapper>
        </SectionWrapper>
      ),
      id: 2,
    });
  }

  if (advice && adviceItems.length > 0) {
    const asList = !!adviceItems[0].card;
    items.push({
      hed: 'Advice',
      dek: (
        <SectionWrapper>
          <AdviceContentWrapper as={asList ? 'ul' : 'div'}>
            {adviceItems.map((adviceItem, index) => (
              <Fragment key={`advice-item-${index}`}>
                {adviceItem.card ? (
                  <>
                    <InnerListItem>
                      <AdviceItem {...adviceItem} />
                    </InnerListItem>
                    {index < adviceItems.length - 1 && <StyledHr aria-hidden />}
                  </>
                ) : (
                  <>
                    <AdviceItem {...adviceItem} />
                    {index < adviceItems.length - 1 && <StyledHr aria-hidden />}
                  </>
                )}
              </Fragment>
            ))}
          </AdviceContentWrapper>
          <ButtonWrapper>
            <ButtonV3 size="large" variant="secondary" component="a" href="/expert-advice">
              Read More
            </ButtonV3>
          </ButtonWrapper>
        </SectionWrapper>
      ),
      id: 3,
    });
  }

  if (!items) {
    return <></>;
  }

  return (
    <Wrapper className={className}>
      <ContentWrapper>
        <StyledHTitle3 color="WHITE_100" presentation="h2">
          Our team is <T.Serif>always</T.Serif> here to support you
        </StyledHTitle3>
        <AccordionV2
          items={items}
          collapsed={collapsed}
          arrowOnLeft={false}
          initialExpandedItems={initialExpandedItems}
        />
      </ContentWrapper>
    </Wrapper>
  );
};

export default SupportAccordion;
